import { ct } from './i18n.js';
/**
 * HTTP client of the dashboard.
 *
 * The old code repeated a `fetch` block in each of the sixteen JS files, with
 * different error handling every time - and most often none at all: the
 * responses were not checked, so a request refused with a 403 was treated as a
 * success and the user saw "Saved".
 * The CSRF token was never sent either.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * What the server attached to the refusal.
   *
   * Dropped on the floor until now. The quota error has carried its figures
   * since the day it was written — `used`, `limit`, and what a subscription
   * would allow — with a comment saying they let the interface show a targeted
   * nudge rather than an anonymous wall. Nothing read them, so the nudge did
   * not exist and the wall was anonymous.
   */
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ApiFailure {
  ok: false;
  error: { code?: string; message?: string; details?: unknown };
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

async function parse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const failure = payload as ApiFailure | null;
    throw new ApiError(
      response.status,
      failure?.error?.code ?? 'UNKNOWN',
      // Translated: this is what the toast shows for a non-JSON failure — a
      // proxy timeout, a body-limit 413 — and it read French in every language.
      failure?.error?.message ??
        ct('requestFailed', 'The request failed ({status}).', { status: String(response.status) }),
      failure?.error?.details,
    );
  }

  return payload as T;
}

async function request(
  method: string,
  url: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const isFormData = body instanceof FormData;

  return fetch(url, {
    method,
    headers: {
      'x-csrf-token': csrfToken(),
      ...(isFormData || body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
    signal,
  });
}

async function send<T>(method: string, url: string, body?: unknown): Promise<T> {
  return parse<T>(await request(method, url, body));
}

export const api = {
  get: <T>(url: string) => send<T>('GET', url),
  /**
   * A response that is not JSON - a generated image, today.
   *
   * Goes through the same builder as the rest so the CSRF token is attached in
   * one place. A hand-rolled `fetch` beside it is how a route ends up quietly
   * unprotected the day the header changes.
   */
  /**
   * The headers come back with the picture: the card preview reads one that
   * says the background fell back to the plain colour, which the PNG alone
   * cannot tell apart from a card that has no background at all.
   */
  postBlob: async (
    url: string,
    body: unknown,
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; headers: Headers }> => {
    const response = await request('POST', url, body, signal);
    if (!response.ok) await parse<unknown>(response);
    return { blob: await response.blob(), headers: response.headers };
  },
  post: <T>(url: string, body?: unknown) => send<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => send<T>('PUT', url, body),
  patch: <T>(url: string, body?: unknown) => send<T>('PATCH', url, body),
  delete: <T>(url: string) => send<T>('DELETE', url),
};
